import { useSettings } from '@/data/Settings';
import { usePathname } from 'next/navigation';
import { sendAdobeTargetView } from 'presentation/events/hooks/adobe/utils';
import { useEffect, useRef } from 'react';
import {
	ADOBE_NOT_ALLOWED_PATH_LIST_KEY,
	ADOBE_TARGET_COMPONENT_VIEWS_ENABLED_KEY,
	ENABLE_ADOBE_SCRIPT_FLAG,
} from 'shared/custom/constants/commonConstants';
import { isScriptAllowedForPath } from 'shared/custom/utils/scriptPathFilter';

/**
 * Fires a single Adobe Target view (WebSDK `sendEvent` with a `viewName`) for a
 * persistent global component (header, footer, etc.) the first time it renders
 * on an allowed path.
 *
 * Storeconf: set userData `ADOBE_TARGET_COMPONENT_VIEWS_ENABLED` to `'true'` to
 * enable this hook. Disabled by default while the value doesn't exist. Also
 * short-circuits globally when `ENABLE_ADOBE_SCRIPT_FLAG` isn't `'true'`.
 */
export const useAdobeTargetComponentView = (viewName: string) => {
	const firedRef = useRef(false);
	const { settings } = useSettings();
	const pathname = usePathname();

	useEffect(() => {
		try {
			const isAdobeScriptEnabled = settings?.userData?.[ENABLE_ADOBE_SCRIPT_FLAG] === 'true';
			if (!isAdobeScriptEnabled) return;

			if (firedRef.current) return;

			const isEnabled = settings?.userData?.[ADOBE_TARGET_COMPONENT_VIEWS_ENABLED_KEY] === 'true';
			if (!isEnabled) return;

			const isAllowed = isScriptAllowedForPath(
				settings?.userData,
				pathname ?? undefined,
				ADOBE_NOT_ALLOWED_PATH_LIST_KEY
			);
			if (!isAllowed) return;

			firedRef.current = true;

			sendAdobeTargetView(viewName);
		} catch (e) {
			console.error('Error sending Adobe Target component view', e);
		}
	}, [pathname, settings?.userData, viewName]);
};
